import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Zap, 
  ArrowLeft,
  Play,
  Trophy,
  Coins,
  RotateCcw,
  Video,
  Sparkles,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { UserModelData, PAGE_NAME } from "../../../types";
import useThemeHook from "../../hooks/useThemeHook";
import CommonPageTitle from "../../components/CommonPageTitle";
import arthereumProvider from "../../../backend/ArthereumProvider";
import { actions } from "../../redux";
import piNetworkAdsManager from "../../utils/PiNetworkAdsManager";

type GameState = "idle" | "playing" | "finished";

const TapRushGame: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useThemeHook();
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;

  const [gameState, setGameState] = useState<GameState>("idle");
  const [taps, setTaps] = useState(0);
  const [timeLeft, setTimeLeft] = useState(10);
  const [earnedATH, setEarnedATH] = useState(0);
  const [isClaimingReward, setIsClaimingReward] = useState(false);
  const [showReward, setShowReward] = useState(false);
  const [highScore, setHighScore] = useState(0);
  const [bonusMultiplier, setBonusMultiplier] = useState(1);
  const [isWatchingAd, setIsWatchingAd] = useState(false);
  const [adWatched, setAdWatched] = useState(false);
  const [showAdGate, setShowAdGate] = useState(false);
  const [adGateLoading, setAdGateLoading] = useState(false);
  
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const tapButtonRef = useRef<HTMLButtonElement>(null);

  // Load high score from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("tapRushHighScore");
    if (saved) setHighScore(parseInt(saved));
  }, []);

  // Check daily play count on mount
  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const key = `game_plays_tap_rush_${user?.id}_${today}`;
    const plays = parseInt(localStorage.getItem(key) || '0', 10);
    if (plays >= 1) {
      setShowAdGate(true);
    }
  }, [user?.id]);

  // Game timer
  useEffect(() => {
    if (gameState === "playing" && timeLeft > 0) {
      timerRef.current = setTimeout(() => {
        setTimeLeft(t => t - 1);
      }, 1000);
    } else if (gameState === "playing" && timeLeft === 0) {
      endGame();
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [gameState, timeLeft]);

  const handleAdGateWatch = async () => {
    setAdGateLoading(true);
    try {
      await piNetworkAdsManager.handleRewardedAdFlowFront(user?.id || '', () => {
        setShowAdGate(false);
      });
    } catch (error) {
      console.error('Ad gate error:', error);
    } finally {
      setAdGateLoading(false);
    }
  };

  const incrementPlayCount = () => {
    const today = new Date().toISOString().split('T')[0];
    const key = `game_plays_tap_rush_${user?.id}_${today}`;
    const plays = parseInt(localStorage.getItem(key) || '0', 10);
    localStorage.setItem(key, (plays + 1).toString());
  };

  const startGame = () => {
    incrementPlayCount();
    setGameState("playing");
    setTaps(0);
    setTimeLeft(10);
    setEarnedATH(0);
    setShowReward(false);
    setBonusMultiplier(1);
    setAdWatched(false);
  };

  const handleTap = useCallback(() => {
    if (gameState !== "playing") return;
    setTaps(t => t + 1);
    
    // Visual feedback
    if (tapButtonRef.current) {
      tapButtonRef.current.style.transform = "scale(0.95)";
      setTimeout(() => {
        if (tapButtonRef.current) {
          tapButtonRef.current.style.transform = "scale(1)";
        }
      }, 50);
    }
  }, [gameState]);

  const endGame = async () => {
    setGameState("finished");
    
    // Calculate ATH reward based on taps (much smaller rewards)
    // 0-20 taps = 0.1 ATH, 21-40 = 0.2 ATH, 41-60 = 0.4 ATH, 61-80 = 0.6 ATH, 80+ = 1 ATH
    let reward = 0.1;
    if (taps >= 80) reward = 1;
    else if (taps >= 60) reward = 0.6;
    else if (taps >= 40) reward = 0.4;
    else if (taps >= 20) reward = 0.2;
    
    setEarnedATH(reward);
    
    // Update high score
    if (taps > highScore) {
      setHighScore(taps);
      localStorage.setItem("tapRushHighScore", taps.toString());
    }

    // Interstitial ad converted to Rewarded Ad for higher payout, triggered right after game ends
    setTimeout(() => {
      if (user?.id) piNetworkAdsManager.handleRewardedAdFlowFront(user.id);
    }, 2000);
  };

  const watchAdForBonus = async () => {
    if (!user?.id || isWatchingAd) return;
    
    setIsWatchingAd(true);
    try {
      await piNetworkAdsManager.handleRewardedAdFlowFront(user.id);
      // If successful, double the reward
      setBonusMultiplier(2);
      setAdWatched(true);
      // Record that this ad was watched for a game bonus
      await arthereumProvider.recordGameAdBonus(user.id, "tap_rush", earnedATH);
    } catch (error) {
      console.error("Ad failed:", error);
    } finally {
      setIsWatchingAd(false);
    }
  };

  const claimReward = async () => {
    if (!user?.id || isClaimingReward) return;
    
    const finalReward = parseFloat((earnedATH * bonusMultiplier).toFixed(2));
    
    setIsClaimingReward(true);
    try {
      await arthereumProvider.awardGameATH(user.id, "tap_rush", finalReward, taps);
      
      // Update Redux locally instead of re-fetching from Firestore
      dispatch(actions.setActualUser({
        ...userData,
        user: {
          ...user,
          arthereumBalance: (user.arthereumBalance || 0) + finalReward,
        },
        miningInfos: userData.miningInfos,
      }));
      
      setShowReward(true);
    } catch (error: any) {
      alert(error.message || "Failed to claim reward");
    } finally {
      setIsClaimingReward(false);
    }
  };

  const playAgain = () => {
    setGameState("idle");
    setTaps(0);
    setTimeLeft(10);
    setEarnedATH(0);
    setShowReward(false);
    setBonusMultiplier(1);
    setAdWatched(false);
  };

  const finalReward = earnedATH * bonusMultiplier;

  return (
    <div className={`min-h-screen ${theme === "dark" ? "bg-slate-900" : "bg-gray-50"}`}>
      {showAdGate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-6">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center space-y-4 shadow-2xl">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
              <Video size={32} className="text-amber-500" />
            </div>
            <h3 className="text-xl font-black text-gray-900">Watch Ad to Play Again!</h3>
            <p className="text-sm text-gray-500">Your free daily play has been used. Watch a short ad to unlock another round!</p>
            <button
              onClick={handleAdGateWatch}
              disabled={adGateLoading}
              className="w-full py-3 rounded-2xl font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 hover:shadow-lg active:scale-95 transition-all"
            >
              {adGateLoading ? 'Loading Ad...' : '🎬 Watch Ad & Play!'}
            </button>
          </div>
        </div>
      )}
      <CommonPageTitle title="Tap Rush" padding={null} />
      
      <div className="max-w-lg mx-auto px-6 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate(PAGE_NAME.GAMES)}
              className={`p-2 rounded-xl ${theme === "dark" ? "bg-slate-800" : "bg-white"}`}
            >
              <ArrowLeft size={20} className={theme === "dark" ? "text-white" : "text-gray-900"} />
            </button>
            <div>
              <h1 className={`text-2xl font-black ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                Tap Rush ⚡
              </h1>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full ${theme === "dark" ? "bg-amber-900/30 text-amber-400" : "bg-amber-100 text-amber-600"} text-sm font-bold`}>
            🏆 Best: {highScore}
          </div>
        </div>

        {/* Game Container */}
        <div className={`
          rounded-3xl p-6 text-center
          ${theme === "dark" 
            ? "bg-gradient-to-br from-yellow-900/30 to-orange-900/30 border border-yellow-500/30" 
            : "bg-gradient-to-br from-yellow-100 to-orange-100 border border-yellow-300"}
        `}>
          {/* Timer & Taps Display */}
          <div className="flex justify-around mb-8">
            <div>
              <p className={`text-xs ${theme === "dark" ? "text-slate-400" : "text-gray-500"} uppercase font-bold`}>Time</p>
              <p className={`text-4xl font-black ${gameState === "playing" && timeLeft <= 3 ? "text-red-500 animate-pulse" : theme === "dark" ? "text-white" : "text-gray-900"}`}>
                {timeLeft}s
              </p>
            </div>
            <div>
              <p className={`text-xs ${theme === "dark" ? "text-slate-400" : "text-gray-500"} uppercase font-bold`}>Taps</p>
              <p className={`text-4xl font-black ${theme === "dark" ? "text-yellow-400" : "text-yellow-600"}`}>
                {taps}
              </p>
            </div>
          </div>

          {/* Game Area */}
          <AnimatePresence mode="wait">
            {gameState === "idle" && (
              <motion.div
                key="idle"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-6"
              >
                <div className={`p-6 rounded-2xl ${theme === "dark" ? "bg-slate-800/50" : "bg-white/50"}`}>
                  <Zap size={64} className="mx-auto text-yellow-400 mb-4" />
                  <h2 className={`text-xl font-bold mb-2 ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                    How to Play
                  </h2>
                  <p className={`text-sm ${theme === "dark" ? "text-slate-400" : "text-gray-500"}`}>
                    Tap the button as fast as you can for 10 seconds!
                    <br />The more taps, the more ATH you earn.
                  </p>
                </div>
                
                <div className="space-y-2">
                  <p className={`text-xs ${theme === "dark" ? "text-slate-500" : "text-gray-400"}`}>Rewards:</p>
                  <div className="flex flex-wrap justify-center gap-2 text-xs">
                    <span className={`px-2 py-1 rounded ${theme === "dark" ? "bg-slate-700 text-slate-300" : "bg-gray-200 text-gray-600"}`}>0-20: 0.1 ATH</span>
                    <span className={`px-2 py-1 rounded ${theme === "dark" ? "bg-slate-700 text-slate-300" : "bg-gray-200 text-gray-600"}`}>21-40: 0.2 ATH</span>
                    <span className={`px-2 py-1 rounded ${theme === "dark" ? "bg-slate-700 text-slate-300" : "bg-gray-200 text-gray-600"}`}>41-60: 0.4 ATH</span>
                    <span className={`px-2 py-1 rounded ${theme === "dark" ? "bg-amber-900/50 text-amber-400" : "bg-amber-100 text-amber-600"}`}>61-80: 0.6 ATH</span>
                    <span className={`px-2 py-1 rounded ${theme === "dark" ? "bg-emerald-900/50 text-emerald-400" : "bg-emerald-100 text-emerald-600"}`}>80+: 1 ATH!</span>
                  </div>
                </div>

                <button
                  onClick={startGame}
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-black text-lg flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-yellow-500/30 transition-all active:scale-95"
                >
                  <Play size={24} /> Start Game
                </button>
              </motion.div>
            )}

            {gameState === "playing" && (
              <motion.div
                key="playing"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
              >
                <button
                  ref={tapButtonRef}
                  onClick={handleTap}
                  className={`
                    w-48 h-48 rounded-full mx-auto
                    bg-gradient-to-br from-yellow-400 to-orange-500
                    shadow-xl shadow-yellow-500/50
                    flex items-center justify-center
                    text-white font-black text-2xl
                    transition-transform
                    active:scale-90
                    select-none
                  `}
                >
                  <Zap size={64} />
                </button>
                <p className={`mt-6 text-lg font-bold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                  TAP! TAP! TAP!
                </p>
              </motion.div>
            )}

            {gameState === "finished" && (
              <motion.div
                key="finished"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-4"
              >
                <div className={`p-6 rounded-2xl ${theme === "dark" ? "bg-slate-800/50" : "bg-white/50"}`}>
                  <Trophy size={64} className="mx-auto text-yellow-400 mb-4" />
                  <h2 className={`text-2xl font-black mb-2 ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                    Time's Up!
                  </h2>
                  <p className={`text-4xl font-black ${theme === "dark" ? "text-yellow-400" : "text-yellow-600"}`}>
                    {taps} Taps
                  </p>
                  {taps > highScore && taps > 0 && (
                    <p className="text-emerald-400 text-sm font-bold mt-2">🎉 New High Score!</p>
                  )}
                </div>

                {/* Reward Display */}
                <div className={`p-4 rounded-2xl ${theme === "dark" ? "bg-emerald-900/30 border border-emerald-500/30" : "bg-emerald-100 border border-emerald-300"}`}>
                  <p className={`text-sm ${theme === "dark" ? "text-emerald-400" : "text-emerald-600"} mb-1`}>You earned:</p>
                  <p className={`text-3xl font-black ${theme === "dark" ? "text-white" : "text-gray-900"} flex items-center justify-center gap-2`}>
                    <Coins className="text-yellow-400" /> 
                    {finalReward.toFixed(2)} ATH
                    {bonusMultiplier > 1 && (
                      <span className="text-emerald-400 text-sm ml-1">({bonusMultiplier}x)</span>
                    )}
                  </p>
                </div>

                {/* Watch Ad for Bonus - Only show if not yet watched */}
                {!showReward && !adWatched && (
                  <motion.button
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    onClick={watchAdForBonus}
                    disabled={isWatchingAd}
                    className={`
                      w-full py-3 rounded-2xl flex items-center justify-center gap-2 font-bold
                      bg-gradient-to-r from-purple-600 to-indigo-600 text-white
                      hover:shadow-lg hover:shadow-purple-500/30 transition-all active:scale-95
                      disabled:opacity-50
                    `}
                  >
                    <Video size={20} />
                    {isWatchingAd ? "Loading Ad..." : (
                      <>
                        Watch Ad for <span className="text-emerald-300">2X BONUS</span>
                        <Sparkles size={16} className="text-yellow-300" />
                      </>
                    )}
                  </motion.button>
                )}

                {/* Bonus Applied Badge */}
                {adWatched && !showReward && (
                  <div className="p-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-center text-sm font-bold">
                    ✨ 2X BONUS APPLIED!
                  </div>
                )}

                {!showReward ? (
                  <button
                    onClick={claimReward}
                    disabled={isClaimingReward}
                    className="w-full py-4 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black text-lg flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-emerald-500/30 transition-all active:scale-95 disabled:opacity-50"
                  >
                    {isClaimingReward ? "Claiming..." : `Claim ${finalReward.toFixed(2)} ATH 🎁`}
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div className={`p-3 rounded-xl ${theme === "dark" ? "bg-emerald-500" : "bg-emerald-500"} text-white text-center font-bold`}>
                      ✅ Reward Claimed!
                    </div>
                    <button
                      onClick={playAgain}
                      className="w-full py-4 rounded-2xl bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-black text-lg flex items-center justify-center gap-2 hover:shadow-lg transition-all active:scale-95"
                    >
                      <RotateCcw size={20} /> Play Again
                    </button>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default TapRushGame;
